import {
  ApolloError,
  BaseMutationOptions,
  DocumentNode,
  LazyQueryResult,
  OperationVariables,
  QueryHookOptions,
  QueryLazyOptions,
  QueryResult,
  useLazyQuery as apolloUseLazyQuery,
  useQuery as apolloUseQuery
} from '@apollo/client'
import { RecordType } from 'app/constants/GlobalTypes'
import { useFeedbacker } from 'app/lib/contexts/Feedbacker'
import { queries, RecordTypeEnum } from 'app/queries'
import { useState } from 'react'

import { isRecordNotFound } from '../apollo/errors'
import { useNormalizer } from '../apollo/normalizers/utilities'
import { getEffectivePageSize, getPageVariablesFromOneBasedPage } from './paginationUtils'
import { useDefaultPaginator, useParamsPaginator } from './usePaginator'
import useTranslation from './useTranslation'

interface Result<T> extends Omit<QueryResult<T>, 'data'> {
  data: T
}

type LazyResult<T> = {
  execute: (options?: QueryLazyOptions<OperationVariables>) => Promise<{ data: T }>
} & LazyQueryResult<T, OperationVariables>

export interface UseQueryOptions<Data, Response = Data> extends Omit<
  QueryHookOptions<Data>,
  'normalizer'
> {
  normalizer?: (response: Response) => Data
}
export interface UseIndexOptions<T> extends UseQueryOptions<T> {
  pageSize?: number
  hasPagination?: boolean
  // Replaces the record type's index query, e.g. with a slimmer projection for
  // views that only render a few fields. Must return the same collection key.
  query?: DocumentNode
  // This props will decide whether pagination params should reflect on the url as search params or not
  paginationWithSearchParams?: boolean
}

/**
 * Error handler hook that displays error messages using toast notifications
 * @param onErrorOption - Optional error callback function
 * @returns Object with onError function that handles Apollo errors
 *
 * @example
 * ```typescript
 * const { onError } = useErrorHandler((error) => {
 *   console.log('Custom error handling:', error)
 * })
 *
 * // Use in query options
 * const { data } = useQuery(MY_QUERY, {
 *   onError: onError
 * })
 * ```
 */
export const useErrorHandler = (onErrorOption: BaseMutationOptions['onError']) => {
  const { toastify } = useFeedbacker()
  const { t } = useTranslation()

  const onError = (error: ApolloError) => {
    // The backend message of a missing record names the model and the id, which says nothing to
    // a customer. Every other error still carries a message written for the user.
    const content = isRecordNotFound(error)
      ? t({ id: 'component.feedback.result.record_not_found.content' })
      : error?.message

    if (typeof content === 'string') {
      toastify({ content, type: 'error' })
    }

    onErrorOption?.(error)
  }

  return { onError }
}

/**
 * Lazy query hook that executes GraphQL queries on demand
 * @template T - The type of data returned by the query
 * @param query - GraphQL query document
 * @param options - Query options including normalizer function
 * @returns Object with execute function and query result state
 *
 * @example
 * ```typescript
 * const { execute, loading, data } = useLazyQuery<{ user: User }>(GET_USER_QUERY)
 *
 * const handleFetchUser = async () => {
 *   try {
 *     const result = await execute({ variables: { id: '123' } })
 *     console.log('User data:', result.data)
 *   } catch (error) {
 *     console.error('Failed to fetch user:', error)
 *   }
 * }
 *
 * return (
 *   <div>
 *     <button onClick={handleFetchUser} disabled={loading}>
 *       {loading ? 'Loading...' : 'Fetch User'}
 *     </button>
 *     {data?.user && <UserProfile user={data.user} />}
 *   </div>
 * )
 * ```
 */
export const useLazyQuery = <T>(
  query: DocumentNode,
  options: UseQueryOptions<T> = {}
): LazyResult<T> => {
  const { normalizer, ...otherOptions } = options
  const { onError } = useErrorHandler(options.onError)
  const [execute, { data, loading, error, ...others }] = apolloUseLazyQuery<T>(query, {
    ...otherOptions,
    onError
  })
  //@ts-ignore
  const { normalizedData } = useNormalizer(data, normalizer)

  return {
    execute,
    data: normalizedData || {},
    loading,
    error,
    ...others
  } as LazyResult<T>
}

/**
 * Query hook for executing GraphQL queries with automatic error handling and data normalization
 * @template Data - The type of normalized data returned to the component
 * @template Response - The type of raw data returned by the server
 * @param query - GraphQL query document
 * @param options - Query options including normalizer function
 * @returns Query result with normalized data and loading/error states
 *
 * @example
 * ```typescript
 * // Basic usage
 * const { data, loading, error } = useQuery<{ rental: IRental }>(FETCH_RENTAL_QUERY, {
 *   variables: { id: 'rental-123' },
 *   fetchPolicy: 'cache-first'
 * })
 *
 * // With data normalization
 * const { data, loading } = useQuery<NormalizedRental, RawRentalData>(
 *   FETCH_RENTAL_QUERY,
 *   {
 *     variables: { id: 'rental-123' },
 *     normalizer: (response) => ({
 *       id: response.rental.id,
 *       title: response.rental.title,
 *       status: response.rental.status.toUpperCase()
 *     })
 *   }
 * )
 * ```
 */
export const useQuery = <Data, Response = Data>(
  query: DocumentNode,
  options: UseQueryOptions<Data, Response> = {}
): Result<Data> => {
  const { normalizer, ...otherOptions } = options
  const { onError } = useErrorHandler(options.onError)
  const { data, loading, error, ...others } = apolloUseQuery(query, {
    ...otherOptions,
    onError
  })
  const { normalizedData } = useNormalizer(data, normalizer)

  return {
    data: normalizedData || {},
    loading,
    error,
    ...others
  }
}

/**
 * Index query hook for fetching paginated lists of records with built-in pagination handling
 * @template T - The type of data returned by the query
 * @param recordType - The type of record to fetch (from RecordTypeEnum)
 * @param options - Query options including pagination settings
 * @returns Query result with paginated data and pagination controls
 *
 * @example
 * ```typescript
 * // Basic usage with default pagination
 * const { data, loading, onPageChange, pageSize } = useIndex<{ rentals: Rental[] }>(
 *   RecordTypeEnum.RENTAL,
 *   {
 *     variables: { status: 'active' },
 *     pageSize: 20
 *   }
 * )
 * ```
 */
export const useIndex = <T>(
  recordType: RecordTypeEnum,
  options: UseIndexOptions<T> = { variables: {} }
) => {
  const {
    pageSize: initalPageSize,
    paginationWithSearchParams = false,
    hasPagination = true,
    query: queryOverride,
    ...otherOptions
  } = options

  const usePaginator = paginationWithSearchParams ? useParamsPaginator : useDefaultPaginator

  const { paginationConfig, onPageChange } = usePaginator({ initalPageSize })

  // Learned from the connection after the first response; used so page count/offset
  // respect graphql-ruby max_page_size clamping.
  const [maxPageSize, setMaxPageSize] = useState<number | null>()

  const query = queryOverride || queries[recordType].index

  const {
    refetch,
    data: responseData,
    ...otherResponses
  } = useQuery<T>(query, {
    fetchPolicy: 'network-only',
    ...otherOptions,
    variables: {
      ...(hasPagination
        ? getPageVariablesFromOneBasedPage(
            paginationConfig.currentPage,
            paginationConfig.pageSize,
            maxPageSize
          )
        : {}),
      ...options.variables
    },
    notifyOnNetworkStatusChange: true
  })

  //@ts-ignore
  const data = responseData ? responseData[RecordType.plurify()[recordType as RecordTypeEnum]] : {}

  // Cache maxPageSize from the connection response for subsequent page requests.
  const serverMaxPageSize = data?.maxPageSize
  if (serverMaxPageSize !== undefined && serverMaxPageSize !== maxPageSize) {
    setMaxPageSize(serverMaxPageSize)
  }

  const effectivePageSize = getEffectivePageSize(paginationConfig.pageSize, maxPageSize)

  return {
    ...otherResponses,
    refetch,
    onPageChange,
    pageSize: paginationConfig.pageSize,
    effectivePageSize,
    data: hasPagination
      ? {
          ...responseData,
          ...data,
          nodes: data?.nodes || [],
          currentPage: paginationConfig.currentPage,
          maxPageSize
        }
      : {
          ...responseData,
          // Index queries without client pagination are either plain arrays or
          // Relay connections (e.g. lineItemsPaginated); read nodes off both.
          nodes: Array.isArray(data) ? data : data?.nodes || []
        }
  }
}
